<div class="header">
  <div class="content">
    <div class="contentInner">
      <div class="pageTitle" appReveal>
        <div class="headline">Dash Enterprise</div>
      </div>
    </div>
    <app-enterprise-tab-navigator></app-enterprise-tab-navigator>
  </div>
</div>
<div class="content">
  <div class="contentInner">
    <h1 class="title">User Administration</h1>
    <p class="intro">Dash Enterprise's user administration features allow you to control who has access to the web management
      dashboard, and the actions they can perform.  You can create users directly in the dashboard, or integrate Dash
      Enterprise with an identity provider for single sign-on.</p>

    <h2 class="sectionHeader">Overview</h2>
    <app-illustration alt="The Dash Enterprise User Management overview page" src="/assets/images/dash-enterprise-user-admin-overview.png" webpSrc="/assets/images/dash-enterprise-user-admin-overview.webp" width="1586" height="1018" class="illustration"></app-illustration>
    <p>
      The user overview shows all the user accounts that exist in your organization.  For each user, you can see their
      current status and level of access, and also perform the following actions:
    </p>
    <ul>
      <li>Editing the user to change their name or roles</li>
      <li>Enabling or disabling the user's sign-in access</li>
      <li>Resetting a user's two-factor authentication (for users who have two-factor authentication enabled on their account)</li>
      <li>Deleting the user account</li>
      <li>Resending the user's invitation email (only available for users that have not yet completed the signup process)</li>
    </ul>

    <h2 class="sectionHeader">Adding users</h2>
    <app-illustration alt="Adding a new user in Dash Enterprise" src="/assets/images/dash-enterprise-new-user.png" webpSrc="/assets/images/dash-enterprise-new-user.webp" width="600" height="733" class="illustration"></app-illustration>
    <p>
      To add a new user, click the <i>New user</i> button in the toolbar.  You'll need to enter the user's first name and
      email address, and decide whether to require two-factor authentication (2FA) for their account.  You can also
      select the roles you want to assign to the user.  The available roles are:
    </p>
    <ul>
      <li><b>User manager - </b>can access the user administration function in the dashboard, and has
      the ability to add, edit, and remove users, as well as set their roles.</li>
      <li><b>Configuration manager - </b>can create, edit and delete device groups and their device configurations.</li>
      <li><b>Device manager - </b>can activate and deactivate Dash display devices and assign them to existing device groups</li>
    </ul>
    <p>
      Roles are additive, meaning that assigning multiple roles to a user will grant them all the associated permissions.
      If a user is not assigned any role, they will have read-only access to the dashboard.
    </p>
    <p>
      Clicking the <i>Invite</i> button will send the user a welcome email containing login instructions and a temporary
      password.  To activate their account, the user must sign in within 4 days and set a permanent password.
    </p>
    <p>
      When the invite email is sent, the user's account status will show as <i>Invited</i>. Once they sign in and choose
      a permanent password, their status will change to <i>Active</i>.  If the user does not sign in within 4 days, you
      can resend the welcome email by clicking the <i>Resend</i> button in the users table.
    </p>
    <h2 class="sectionHeader">Editing users</h2>
    <p>
      To edit a user, select the user in the table and click the <i>Edit</i> button in the toolbar.  You can change the user's display name as well as their roles.
    </p>
    <p class="tip icon">Note that it can take up to 1 hour for role changes to take effect.  The user can refresh their roles immediately by signing out of the dashboard and signing in again.</p>

    <h2 class="sectionHeader">Resetting a user's two-factor authentication</h2>
    <p>
      If a user has lost their authenticator device or is otherwise unable to enter their code when signing in, you can
      disable two-factor authentication (2FA) on their account to allow them to access the dashboard. Simply click
      <i>Reset</i> in the <i>2FA status</i> column of the user table, and they will be able to sign in.
    </p>

    <p class="tip icon">If the user's account is set to require 2FA, they'll be prompted to set up a new authenticator
      when they sign in.  If 2FA is optional for their account, they can set it up themselves from the account details menu.
    </p>

    <h2 class="sectionHeader">Disabling access and deleting accounts</h2>
    <p>
      There are two ways to disable a user's access to the dashboard:
    </p>
    <ul>
      <li><b>Disable account access - </b>To temporarily disable a user's access, click the <i>Disable</i> button in the <i>Account Access</i> column of the users table. This action keeps the account in the system but removes the user's ability to sign in. It is useful for temporarily restricting access.</li>
      <li><b>Delete the account - </b>To permanently delete a user's account, click the trash can icon in the users table. This action will remove the account from the system permanently.</li>
    </ul>
    <p class="tip icon">Note that a user cannot disable or delete their own account.  A user account can only be deleted by another user with the <i>User manager</i> role.</p>


    <h2 class="sectionHeader">Single Sign-on (SSO)</h2>
    <p>Dash Enterprise can be configured to connect to your corporate identity provider (IDP) using either SAML 2.0 or
      OpenID Connect.  If you have a Dash Enterprise subscription and would like to enable SSO on your account,
      please <a routerLink="/contact">contact Dash support</a>.  To set up a SSO integration, click <i>Set up single
        sign-on (SSO)</i> in the main toolbar, and select the type of SSO integration you would like to set up.</p>

    <h4>SAML 2.0</h4>
    <p>Configure your IDP using the values provided in the setup dialog.  Once your IDP is configured, you can pass the
      SAML metadata document to Dash Enterprise either by entering a URL or by uploading the document directly.  You can
      optionally require that SAML assertions from your IDP are signed and/or sign SAML requests from Dash Enterprise to
      your IDP.</p>

    <p class="tip icon">If you choose to enable signing, the necessary certificates will be available after you create the SSO configuration,
      so just save the configuration then click "Edit SSO Configuration" to retrieve them.</p>

    <h5>User attributes</h5>
    <p>Dash Enterprise requires you to specify the user attributes that contain the user's first name and email address.
      You can specify them in the format you use in your IDP.  For example, you could enter <i>emailAddress</i> if the
      attribute is of the <i>Basic</i> type, or <i>http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress</i>
      if the attribute is of the <i>URI</i> type.</p>

    <h5>Default permissions</h5>
    <p>Dash Enterprise will assign default permissions to each user when they first sign in.  You can either specify the
      permissions that will be applied, or you can specify SAML user attributes that will be used to set the permissions
      for each user.  Configure your IDP so that the attributes have the value 'true' if you would like the user to have
      the given permission.</p>

    <p class="tip icon">If you choose to specify the default permissions, they will be set once (when the user first
      signs in) and can then be modified by editing their account from the users table.  If you set permissions
      from SAML attributes, the permissions cannot be edited from the web dashboard, but will be automatically updated
      each time the user signs in.</p>

    <h4>Open ID Connect (OIDC)</h4>
    <p>Configure your IDP with the scopes and redirect URI provided in the setup dialog.  Once your IDP is configured,
      enter the domain of your IDP's OpenID configuration document.  For example, if the document is located at
      <i>https://yourdomain.provider.com/.well-known/openid-configuration</i> then enter <i>https://yourdomain.provider.com</i></p>

    <h5>User attributes</h5>
    <p>Dash Enterprise requires you to specify the user attributes that contain the user's first name and email address.
      If you do not know the request method or field names, leave them at their default values.</p>

    <h5>Default permissions</h5>
    <p>Dash Enterprise will assign default permissions to each user when they first sign in.  The permissions you select
      will be applied to each user when they first sign in.  Once they have signed in, you can change the user's permissions
    by editing their account from the users table.</p>

  </div>
</div>
